import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";

type RadioButtonOption = {
  value: string;
  label: string;
};

type RadioButtonsGroupProps = {
  value: string;
  options: RadioButtonOption[];
  onChange: (nextValue: string) => void;
  ariaLabel?: string;
  allowKeyboardDeselect?: boolean;
};

const visuallyHiddenRadioSx = {
  position: "absolute",
  opacity: 0,
  width: 1,
  height: 1,
  m: 0,
  p: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
} as const;

export function RadioButtonsGroup({
  value,
  options,
  onChange,
  ariaLabel = "options",
  allowKeyboardDeselect = false,
}: RadioButtonsGroupProps) {
  const optionValues = new Set(options.map((option) => option.value));

  return (
    <RadioGroup
      aria-label={ariaLabel}
      value={value}
      onChange={(event) => {
        const nextValue = event.target.value;
        if (!optionValues.has(nextValue)) {
          return;
        }
        onChange(nextValue);
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") {
          return;
        }
        const target = event.target as HTMLInputElement;
        if (target?.type !== "radio") {
          return;
        }
        if (!optionValues.has(target.value)) {
          return;
        }
        event.preventDefault();
        if (allowKeyboardDeselect && value === target.value) {
          onChange("");
          return;
        }
        onChange(target.value);
      }}
    >
      <Stack direction="row" justifyContent="center" gap={2}>
        {options.map((option) => {
          const isSelected = value === option.value;
          return (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={
                <Radio
                  value={option.value}
                  disableRipple
                  sx={visuallyHiddenRadioSx}
                  inputProps={{ "aria-label": option.label }}
                />
              }
              sx={{
                m: 0,
                position: "relative",
                ".MuiFormControlLabel-label": { width: "100%" },
                ".MuiRadio-root.Mui-focusVisible + .MuiFormControlLabel-label .MuiButton-root":
                  {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: 2,
                  },
              }}
              label={
                <Button
                  component="span"
                  size="medium"
                  variant={isSelected ? "contained" : "outlined"}
                  sx={{ px: 5 }}
                >
                  {option.label}
                </Button>
              }
            />
          );
        })}
      </Stack>
    </RadioGroup>
  );
}
